import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Search, Info, ExternalLink, Eraser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/custom/ConfirmDialog';
import { searchDataSubject, eraseTicketContactApi } from '@/hooks/useAdmin';
import type { SubjectSearchHit } from '@uniticket/shared';

/**
 * Data-subject request console (GDPR Art. 15/17).
 *
 * Only the four structured contact_* columns are erased by the button here. Free text and
 * attachments are surfaced as a per-ticket review checklist and redacted by hand through the
 * ticket page — the UI says so explicitly, because an admin who believes this button erased
 * everything would close a request that is not actually complete.
 */

export function PrivacyRequestsPage() {
  const { t } = useTranslation('admin');

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [results, setResults] = useState<SubjectSearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [eraseTarget, setEraseTarget] = useState<SubjectSearchHit | null>(null);

  const canSearch = Boolean(email.trim() || phone.trim() || name.trim());

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!canSearch) return;
    setSearching(true);
    try {
      const res = await searchDataSubject({
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        name: name.trim() || undefined,
      });
      setResults(res.data);
    } catch (err: any) {
      toast.error(err.message || t('privacy.searchError'));
    } finally {
      setSearching(false);
    }
  }

  async function handleEraseConfirm() {
    if (!eraseTarget) return;
    try {
      const res = await eraseTicketContactApi(eraseTarget.id);
      if (res.data.erased) {
        toast.success(t('privacy.erased', { ticket: res.data.ticketNumber }));
      } else {
        toast.info(t('privacy.alreadyErased', { ticket: res.data.ticketNumber }));
      }
      // Reflect the change locally rather than re-running the search — the search term no longer
      // matches an erased ticket, so a refetch would make the row vanish before the admin has
      // finished the manual review it still needs.
      setResults((prev) =>
        prev
          ? prev.map((r) =>
              r.id === eraseTarget.id
                ? {
                    ...r,
                    hasContactData: false,
                    contactFirstName: null,
                    contactLastName: null,
                    contactPhone: null,
                    contactEmail: null,
                  }
                : r,
            )
          : prev,
      );
    } catch (err: any) {
      toast.error(err.message || t('privacy.eraseError'));
    } finally {
      setEraseTarget(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('privacy.title')}</h2>
          <p className="text-sm text-slate-500">{t('privacy.subtitle')}</p>
        </div>
      </div>

      {/* Scope explainer — this is a control, not decoration. */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
        <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 space-y-1.5">
          <p className="font-semibold">{t('privacy.scopeTitle')}</p>
          <p>{t('privacy.scopeAutomatic')}</p>
          <p>{t('privacy.scopeManual')}</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="dsar-email">{t('privacy.email')}</Label>
            <Input
              id="dsar-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('privacy.emailPlaceholder')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dsar-phone">{t('privacy.phone')}</Label>
            <Input
              id="dsar-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('privacy.phonePlaceholder')}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dsar-name">{t('privacy.name')}</Label>
            <Input
              id="dsar-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('privacy.namePlaceholder')}
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button type="submit" disabled={!canSearch || searching}>
            <Search className="w-4 h-4 mr-2" />
            {searching ? t('privacy.searching') : t('privacy.search')}
          </Button>
          <p className="text-xs text-slate-400">{t('privacy.searchHint')}</p>
        </div>
      </form>

      {/* Results */}
      {results !== null && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-700">
              {t('privacy.resultCount', { count: results.length })}
            </p>
          </div>

          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">{t('privacy.noResults')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left font-medium px-4 py-2.5">{t('privacy.cols.ticket')}</th>
                    <th className="text-left font-medium px-4 py-2.5">{t('privacy.cols.contact')}</th>
                    <th className="text-left font-medium px-4 py-2.5">{t('privacy.cols.review')}</th>
                    <th className="text-right font-medium px-4 py-2.5">{t('privacy.cols.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-3">
                        <Link
                          to={`/tickets/${r.id}`}
                          className="font-semibold text-slate-800 hover:text-[#ed125f] inline-flex items-center gap-1"
                        >
                          {r.ticketNumber}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {r.ticketType.replace(/_/g, ' ')} · {r.status.replace(/_/g, ' ')}
                        </p>
                        {r.isDeleted && (
                          <Badge variant="red" className="mt-1 text-[10px]">
                            {t('privacy.softDeleted')}
                          </Badge>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {r.hasContactData ? (
                          <div className="text-slate-700 space-y-0.5">
                            {(r.contactFirstName || r.contactLastName) && (
                              <p>{[r.contactFirstName, r.contactLastName].filter(Boolean).join(' ')}</p>
                            )}
                            {r.contactEmail && <p className="text-xs text-slate-500">{r.contactEmail}</p>}
                            {r.contactPhone && <p className="text-xs text-slate-500">{r.contactPhone}</p>}
                          </div>
                        ) : (
                          <span className="text-xs font-medium text-emerald-600">
                            {t('privacy.contactErased')}
                          </span>
                        )}
                      </td>

                      {/* The manual half of the request. */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {r.hasDescription && (
                            <Badge variant="amber" className="text-[10px]">
                              {t('privacy.hasDescription')}
                            </Badge>
                          )}
                          {r.commentCount > 0 && (
                            <Badge variant="amber" className="text-[10px]">
                              {t('privacy.comments', { count: r.commentCount })}
                            </Badge>
                          )}
                          {r.attachmentCount > 0 && (
                            <Badge variant="amber" className="text-[10px]">
                              {t('privacy.attachments', { count: r.attachmentCount })}
                            </Badge>
                          )}
                          {!r.hasDescription && r.commentCount === 0 && r.attachmentCount === 0 && (
                            <span className="text-xs text-slate-300">{t('privacy.nothingToReview')}</span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!r.hasContactData}
                          onClick={() => setEraseTarget(r)}
                        >
                          <Eraser className="w-3.5 h-3.5 mr-1.5" />
                          {t('privacy.erase')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {eraseTarget && (
        <ConfirmDialog
          title={t('privacy.eraseTitle')}
          message={t('privacy.eraseConfirm', { ticket: eraseTarget.ticketNumber })}
          details={[
            t('privacy.eraseDetailFields'),
            t('privacy.eraseDetailKept'),
            t('privacy.eraseDetailManual'),
          ]}
          confirmLabel={t('privacy.erase')}
          variant="danger"
          onConfirm={handleEraseConfirm}
          onCancel={() => setEraseTarget(null)}
        />
      )}
    </div>
  );
}
